import { API } from './api';
import { END_POINTS } from './endpoints';

/**
 * Mapping of card types to the permission required to confirm them.
 * Mirrors oni-service-realtime's cardConfirmPermissions.ts as per spec.
 */
export const CARD_PERMISSION_MAP = {
  bpChart: 'VITALS_CREATE',
  pulseChart: 'VITALS_CREATE',
  temperatureChart: 'VITALS_CREATE',
  spo2Chart: 'VITALS_CREATE',
  weightGainChart: 'VITALS_CREATE',
  conditions: 'CONSULTATION_CREATE',
  risks: 'CONSULTATION_CREATE',
  observation: 'TREATMENT_CREATE',
  supplementPrescription: 'PRESCRIPTION_CREATE',
  symptomPrescription: 'PRESCRIPTION_CREATE',
  antenatalTest: 'TEST_CREATE',
  nextAntenatalTest: 'TEST_CREATE',
  generalExamination: 'CONSULTATION_CREATE',
  perAbdomen: 'CONSULTATION_CREATE',
  perVagina: 'CONSULTATION_CREATE',
  fetus: 'CONSULTATION_CREATE',
  injection: 'CONSULTATION_CREATE',
  pastMedicalHistory: 'HISTORY_CREATE',
  familyHistory: 'HISTORY_CREATE',
  pastDiseaseHistory: 'HISTORY_CREATE',
  currentPregnancy: 'HISTORY_CREATE',
  gtpla: 'HISTORY_CREATE',
  termHistory: 'HISTORY_CREATE',
  abortionHistory: 'HISTORY_CREATE',
  pregnancyConfirmation: 'HISTORY_CREATE',
  gynecologyProfile: 'HISTORY_CREATE',
};

/**
 * Friendly category names and card metadata for display
 */
export const CARD_CATEGORIES = {
  VITALS: ['bpChart', 'pulseChart', 'temperatureChart', 'spo2Chart', 'weightGainChart'],
  CONDITIONS_RISKS: ['conditions', 'risks'],
  CLINICAL_NOTES: ['observation'],
  PRESCRIPTIONS: ['supplementPrescription', 'symptomPrescription'],
  TESTS: ['antenatalTest', 'nextAntenatalTest'],
  EXAMINATION: ['generalExamination', 'perAbdomen', 'perVagina', 'fetus', 'injection'],
  HISTORY: [
    'pastMedicalHistory',
    'familyHistory',
    'pastDiseaseHistory',
    'currentPregnancy',
    'gtpla',
    'termHistory',
    'abortionHistory',
    'pregnancyConfirmation',
    'gynecologyProfile',
  ],
};

export const getCardCategory = (cardType) => {
  for (const [category, types] of Object.entries(CARD_CATEGORIES)) {
    if (types.includes(cardType)) return category;
  }
  return 'OTHER';
};

/**
 * Fetch pending AI consultation cards for a consultation.
 * GET /v1/consultations/:consultationId/ai-cards
 */
export const getPendingAiCards = async (consultationId) => {
  if (!consultationId) {
    return { success: false, message: 'Consultation ID is required' };
  }

  try {
    const response = await API.get(END_POINTS.getAiCards(consultationId));

    // Handle standard wrapper or direct payload
    const data = response?.data || response;

    if (response?.success === false) {
      return {
        success: false,
        status: response.status,
        errorCode: response.errorCode,
        message: response.message || 'Failed to load AI consultation cards',
      };
    }

    return {
      success: true,
      consultationId: data?.consultationId || consultationId,
      patientId: data?.patientId,
      cards: Array.isArray(data?.cards) ? data.cards : [],
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || 'Error fetching AI cards',
    };
  }
};

/**
 * Normalize data payload for a card according to spec:
 * Build data from the card's top-level fields: data = { [field.key]: field.value }
 * - conditions: { patientConditionIds: string[] }
 * - all other types: plain data object { [key]: value }
 */
export const normalizeCardData = (cardType, cardData) => {
  let data = cardData?.data !== undefined ? cardData.data : cardData;

  if (cardType === 'conditions') {
    if (data && typeof data === 'object' && Array.isArray(data.patientConditionIds)) {
      return data;
    }
    let ids = [];
    if (Array.isArray(data)) {
      ids = data.map((c) => (typeof c === 'string' ? c : c?.patientConditionId)).filter(Boolean);
    } else if (data?.patientConditionId) {
      ids = [data.patientConditionId];
    } else if (data && typeof data === 'object') {
      const arr = Object.values(data).find(Array.isArray);
      if (arr) ids = arr;
    }
    return { patientConditionIds: ids };
  }

  return data;
};

/**
 * Format payload helper (kept for backward compatibility and testing)
 */
export const formatConfirmPayload = (cardType, cardData) => {
  if (cardType === 'conditions') {
    return normalizeCardData('conditions', cardData);
  }
  const data = cardData?.data !== undefined ? cardData.data : cardData;
  return { data };
};

/**
 * Confirm multiple cards in a single batch call.
 * POST /v1/consultations/:consultationId/ai-cards/confirm
 * Body: { "cards": [ { "cardType": "...", "data": { ... } } ] }
 */
export const confirmAllAiCards = async (consultationId, cards = []) => {
  if (!consultationId || !cards || cards.length === 0) return [];

  const cardsPayload = cards.map((c) => {
    const cardType = c.cardType || c.type;
    const data = normalizeCardData(cardType, c.data !== undefined ? c.data : c);
    return {
      cardType,
      data,
    };
  });

  try {
    const response = await API.post(END_POINTS.confirmAiCards(consultationId), {
      cards: cardsPayload,
    });

    const rawList = response?.data || response;
    let items = [];
    if (Array.isArray(rawList)) {
      items = rawList;
    } else if (Array.isArray(rawList?.cards)) {
      items = rawList.cards;
    } else if (Array.isArray(rawList?.results)) {
      items = rawList.results;
    } else if (rawList && typeof rawList === 'object' && rawList.cardType) {
      items = [rawList];
    }

    return cardsPayload.map((req) => {
      const match = items.find((item) => item?.cardType === req.cardType);

      if (match) {
        const isAlreadyConfirmed = match.errorCode === 'CARD_ALREADY_CONFIRMED';
        const isSuccess = match.success === true || isAlreadyConfirmed;
        return {
          cardType: req.cardType,
          success: isSuccess,
          confirmed: isSuccess,
          alreadyConfirmed: isAlreadyConfirmed,
          errorCode: match.errorCode,
          message:
            match.message ||
            (isAlreadyConfirmed ? 'Card already confirmed' : 'Confirmed successfully'),
          raw: match,
        };
      }

      // Check top-level response errors / codes
      if (response?.errorCode === 'CARD_ALREADY_CONFIRMED' || response?.status === 409) {
        return {
          cardType: req.cardType,
          success: true,
          confirmed: true,
          alreadyConfirmed: true,
          message: 'Card already confirmed',
        };
      }

      if (response?.success !== false && (!response?.errorCode || response.errorCode === 'OK')) {
        return {
          cardType: req.cardType,
          success: true,
          confirmed: true,
          message: 'Confirmed successfully',
        };
      }

      return {
        cardType: req.cardType,
        success: false,
        status: response?.status,
        errorCode: response?.errorCode,
        message: response?.message || `Failed to confirm ${req.cardType}`,
      };
    });
  } catch (error) {
    return cardsPayload.map((req) => ({
      cardType: req.cardType,
      success: false,
      message: error?.message || `Failed to confirm ${req.cardType}`,
    }));
  }
};

/**
 * Confirm a single AI card.
 * POST /v1/consultations/:consultationId/ai-cards/confirm
 * Body: { "cards": [ { "cardType": "...", "data": { ... } } ] }
 */
export const confirmAiCard = async (consultationId, cardType, cardData) => {
  if (!consultationId || !cardType) {
    return { success: false, message: 'Consultation ID and card type are required' };
  }

  const results = await confirmAllAiCards(consultationId, [{ cardType, data: cardData }]);
  const result = results.find((r) => r.cardType === cardType) || results[0];

  return result || { success: false, cardType, message: `Failed to confirm ${cardType}` };
};

/**
 * Save draft cards (any number of times).
 * PATCH /v1/consultations/:consultationId/ai-cards
 * Body: { "cards": [ { "cardType": "...", "data": { ... } } ] }
 */
export const saveAiCards = async (consultationId, cards = []) => {
  if (!consultationId || !cards || cards.length === 0) return [];

  const cardsPayload = cards.map((c) => {
    const cardType = c.cardType || c.type;
    const data = normalizeCardData(cardType, c.data !== undefined ? c.data : c);
    return {
      cardType,
      data,
    };
  });

  try {
    const response = await API.patch(END_POINTS.saveAiCards(consultationId), {
      cards: cardsPayload,
    });

    const rawList = response?.data || response;
    let items = [];
    if (Array.isArray(rawList)) {
      items = rawList;
    } else if (Array.isArray(rawList?.cards)) {
      items = rawList.cards;
    } else if (Array.isArray(rawList?.results)) {
      items = rawList.results;
    } else if (rawList && typeof rawList === 'object' && rawList.cardType) {
      items = [rawList];
    }

    return cardsPayload.map((req) => {
      const match = items.find((item) => item?.cardType === req.cardType);

      if (match) {
        const isAlreadyConfirmed = match.errorCode === 'CARD_ALREADY_CONFIRMED';
        return {
          cardType: req.cardType,
          success: match.success === true,
          alreadyConfirmed: isAlreadyConfirmed,
          errorCode: match.errorCode,
          message: match.message || (isAlreadyConfirmed ? 'Card already confirmed' : 'Draft saved'),
          raw: match,
        };
      }

      if (response?.success !== false) {
        return {
          cardType: req.cardType,
          success: true,
          message: 'Draft saved successfully',
        };
      }

      return {
        cardType: req.cardType,
        success: false,
        errorCode: response?.errorCode,
        message: response?.message || `Failed to save draft for ${req.cardType}`,
      };
    });
  } catch (error) {
    return cardsPayload.map((req) => ({
      cardType: req.cardType,
      success: false,
      message: error?.message || `Failed to save draft for ${req.cardType}`,
    }));
  }
};
